import { getPasswordRequirements, type PasswordContext } from '@/lib/passwordStrength';
import { cn } from '@/lib/utils';
import { WorklynxIcon } from '@/components/icons/WorklynxIcon';
import type { WorklynxIconName } from '@/components/icons/iconData';

interface PasswordRequirementListProps {
  password: string;
  context?: PasswordContext;
  className?: string;
}

export function PasswordRequirementList({
  password,
  context,
  className,
}: PasswordRequirementListProps) {
  const requirements = getPasswordRequirements(password, context);
  const hasInput = password.length > 0;

  return (
    <div
      className={cn(
        'rounded-md border border-brand-border/60 bg-muted/20 p-3 text-left',
        className,
      )}
    >
      <p className="mb-2 text-xs font-semibold uppercase text-muted">
        Password must include
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {requirements.map((requirement) => {
          const met = hasInput && requirement.met;
          const icon: WorklynxIconName = met ? 'success' : 'error';

          return (
            <div
              key={requirement.key}
              className={cn(
                'flex items-center gap-2 text-xs',
                met ? 'text-success' : 'text-muted',
              )}
            >
              <WorklynxIcon name={icon} size={14} className="shrink-0" />
              <span>{requirement.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface PasswordMatchHintProps {
  password: string;
  confirmPassword: string;
}

export function PasswordMatchHint({ password, confirmPassword }: PasswordMatchHintProps) {
  if (!confirmPassword) return null;

  const matches = password === confirmPassword;

  return (
    <div
      className={cn(
        'flex items-center gap-2 text-xs',
        matches ? 'text-success' : 'text-danger',
      )}
    >
      {matches ? (
        <WorklynxIcon name="success" size={14} className="shrink-0" />
      ) : (
        <WorklynxIcon name="error" size={14} className="shrink-0" />
      )}
      <span>{matches ? 'Passwords match' : 'Passwords do not match yet'}</span>
    </div>
  );
}
